import { useState, useEffect } from 'react';

/**
 * Reactive media query hook using window.matchMedia.
 * Re-renders the component when the media query match state changes.
 * @param {string} query - CSS media query string (e.g., '(max-width: 767px)')
 * @returns {boolean} Whether the media query currently matches
 */
export function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e) => setMatches(e.matches);

    setMatches(mql.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

/** @returns {boolean} true when viewport is < 768px (mobile) */
export function useIsMobile() {
  return useMediaQuery('(max-width: 767px)');
}

/** @returns {boolean} true when viewport is 768px–1023px (tablet) */
export function useIsTablet() {
  return useMediaQuery('(min-width: 768px) and (max-width: 1023px)');
}

/** @returns {boolean} true when viewport is >= 1024px (desktop) */
export function useIsDesktop() {
  return useMediaQuery('(min-width: 1024px)');
}
